//A generic quiz component that feeds a set of questions to the specific quiz type, which is invoked as a subcomponent, keeps track of the session statistics and holds overall quiz state. This component accepts parameters that define the number of questions, mixture of questions, etc.
<template>
  <v-container fluid pa-0>
    <v-slide-y-transition mode="out-in">
      <v-layout column align-space-around>
        <v-flex v-if="queue && !quizComplete">
          <v-progress-linear v-model="progress"></v-progress-linear>
          <v-btn small icon @click="exit">
            <v-icon color="black">arrow_back</v-icon>
          </v-btn>
          <v-btn small icon @click="toggleSound">
            <v-icon v-show="soundOn" color="black">volume_up</v-icon>
            <v-icon v-show="!soundOn" color="black">volume_off</v-icon>
          </v-btn>
          <span v-if="isMobile()" style="font-size: 150%; position: absolute; left: 50%; transform: translate(-50%, 0%);"> What is this? </span>
          <component
            v-bind:is="currentQuestionComponent"
            v-bind:prompt="currentQuestion"
            v-on:check="handleAnswer"
            v-on:next="next"
            v-bind:controlled="true"
            :isLocked="false"
          ></component>
        </v-flex>
        <v-flex v-if="quizComplete">
          <v-flex xs12 sm6 offset-sm3>
            <v-container text-xs-center>
              <v-flex
                style="font-size: 40px; font-weight: bold; color: #3c5473"
              >
                <v-layout justify-center>Practice Complete!</v-layout>
                <v-img :src="sheepImage" height="300px">
                  <v-layout column fill-height></v-layout>
                </v-img>
                <div style="font-size: 28px; color: #3c5473; margin: 50px 0" class="percent">
                  <div>Results:</div>
                  <span style="font-size: 20px">&nbsp;{{ correctPercent }} Correct</span> 
                </div>
                <v-layout
                  ><v-btn
                    round
                    outline
                    large
                    block
                    color="primary"
                    @click="resetRound"
                    >Continue</v-btn
                  >
                </v-layout>
              </v-flex>
              <v-card flat>
                <v-card-actions>
                  <v-btn
                    large
                    block
                    round
                    outline
                    lg
                    color="#3C5473"
                    justify-center
                    @click="goBack()"
                    >Finish</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-container>
          </v-flex>

          <v-alert :value="!queue" type="error">
            There has been a problem retrieving your review queue. (T_T)
          </v-alert>
        </v-flex>
      </v-layout>
    </v-slide-y-transition>
  </v-container>
</template>


<script>
import { PRACTICE_ROUND_LENGTH } from "@/constants.js";
import MultipleChoiceQuiz from "./QuizTypes/MultipleChoiceQuiz";
import VocabTypedResponse from "./QuizTypes/VocabTypedResponse";
import ParametersToConjugation from "./QuizTypes/ParametersToConjugation";
import ConjugationToParameters from "./QuizTypes/ConjugationToParameters";
import TranslationToConjugation from "./QuizTypes/TranslationToConjugation";
import ConjugationToTranslation from "./QuizTypes/ConjugationToTranslation";
import FridgeMagnet from "./QuizTypes/FridgeMagnet";
import PaleographyQuestion from "./QuizTypes/PaleographyQuestion";
import NounQuestion from "./QuizTypes/NounQuestion.vue";
export default {
  components: {
    VocabTypedResponse,
    FridgeMagnet,
    MultipleChoiceQuiz,
    ParametersToConjugation,
    ConjugationToParameters,
    TranslationToConjugation,
    ConjugationToTranslation,
    PaleographyQuestion,
    NounQuestion,
  },
  name:"PracticeQuiz",
  data: function () {
    return {
      currentQuestionIndex: 0,
      quizComplete: false,
      queue:
        this.$store.state.practiceQueue.length > 0
          ? this.$store.state.practiceQueue
          : _.shuffle(this.$store.getters.availableItems),
      quizResults: [],
      componentMap: [
        { type: "fm", component: FridgeMagnet },
        { type: "vocab", component: VocabTypedResponse },
        {
          type: "parameters to conjugation",
          component: ParametersToConjugation,
        },
        {
          type: "conjugation to parameters",
          component: ConjugationToParameters,
        },
        {
          type: "translation to conjugation",
          component: TranslationToConjugation,
        },
        {
          type: "conjugation to translation",
          component: ConjugationToTranslation,
        },
        {
          type: "paleoquiz",
          component: PaleographyQuestion,
        },
        {
          type: "noun",
          component: NounQuestion,
        },
      ],
      quizType: this.$route.params.type,
      correctSFX: new Audio(require("../assets/correct.mp3")),
      wrongSFX: new Audio(require("../assets/wrong.wav")),
      soundOn: true,
      practiceTypes: ["Regular", "Verb", "Custom"],
      sheepImage: require("@/../static/img/sheep_photo.jpg"),
    };
  },
  methods: {
    exit: function(){
      this.goBack();
    },
    continueRound: function () {
      this.quizComplete = false;
    },
    resetRound: function () {
      this.quizComplete = false;
      this.queue.splice(0,PRACTICE_ROUND_LENGTH);
      if(this.queue.length < PRACTICE_ROUND_LENGTH){
        console.log("reset queue")
        this.$store.dispatch("generatePracticeQuestions",{type: this.quizType, level: this.queue[0].level, rolloverElements: this.queue});
        this.queue = this.$store.state.practiceQueue;
      }
      this.currentQuestionIndex = 0;
      this.quizResults = [];
    },
    goBack() {
      window.history.back();
    },
    toggleSound: function () {
      this.soundOn = !this.soundOn;
    },
    handleAnswer: function (e) {
      this.quizResults.push(e.correct);
      if (e.correct) {
        if (this.soundOn) {
          this.correctSFX.play();
        }
      } else if (this.soundOn) {
        this.wrongSFX.play();
      }
    },
    next: function () {
      if (this.currentQuestionIndex < PRACTICE_ROUND_LENGTH - 1 && this.currentQuestionIndex < this.queue.length) {
        this.currentQuestionIndex++;
      } else {
        this.handleComplete();
      }
    },
    handleComplete: function () {
      this.quizComplete = true;
    },
    isMobile() {
      if(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        return true
      } else {
        return false
      }
    },
  },
  computed: {
    progress: function () {
      return (this.currentQuestionIndex / PRACTICE_ROUND_LENGTH) * 100;
    },
    correctPercent: function () {
      if (!this.$store.state.loading){
        var percent = (this.quizResults.filter((q) => q).length / PRACTICE_ROUND_LENGTH) * 100
        var percentstring = percent + "%"
        return percentstring
      }
      else return 0;
    },
    currentQuestion: function () {
      return this.queue[this.currentQuestionIndex];
    },
    currentQuestionType: function () {
      if (this.currentQuestion && this.currentQuestion.type) return this.currentQuestion.type;
    },
    currentQuestionComponent: function () {
      if(this.currentQuestion && this.currentQuestionType)
      return this.componentMap.find((x) => x.type === this.currentQuestionType)
        .component;
    },
  },
};
</script>

<style scoped>
.v-progress-linear {
  margin-top: 0;
}
</style>